import { sdkEvents } from '../core/events.js';
import { applyAmmActivityToOutcome } from './apply-trade-to-outcome.js';
import { readIntegerString } from './live-bucket-core.js';

interface AmmPoolState {
  readonly quoteVaultReserveD6: string;
  readonly outcomeVaultReserveD6: string;
}

/**
 * Applies one mint-keyed CP-Swap event to a market-detail row.
 *
 * The pre-persist AMM payload leaves `outcomeIdx` at its protobuf default,
 * so the concrete subject mint is the only authoritative outcome identity.
 * Codegen passes that first subject capture as the fourth helper argument.
 */
export function applyAmmActivityToMarket<
  T extends {
    readonly pubkey?: string;
    readonly liquidityD6?: string;
    readonly outcomes?: ReadonlyArray<{
      readonly mint?: string;
      readonly price?: string;
      readonly indexedOutcomeMintSupplyD6?: string;
      readonly effectiveMarketCapD6?: string;
      readonly cpQuoteReserveD6?: string;
      readonly volumeTotal?: string;
      readonly graduated?: boolean;
      readonly currentPoolState?: object;
    }>;
  },
>(prev: T, raw: Record<string, unknown>, _args: Record<string, unknown>, outcomeMint?: string): T {
  if (outcomeMint === undefined || !Array.isArray(prev.outcomes)) return prev;

  const outcomeIndex = prev.outcomes.findIndex((outcome) => outcome.mint === outcomeMint);
  if (outcomeIndex < 0) return prev;

  const currentOutcome = prev.outcomes[outcomeIndex];
  if (currentOutcome === undefined) return prev;
  if (!isAmmPoolState(currentOutcome.currentPoolState)) {
    rejectMalformedPoolEvent(prev, raw, 'pool_state');
    return prev;
  }
  const quoteReserveD6 = positiveIntegerField(raw, 'postQuoteReservesD6');
  const outcomeReserveD6 = positiveIntegerField(raw, 'postOutcomeReservesD6');
  if (quoteReserveD6 === null || outcomeReserveD6 === null) {
    rejectMalformedPoolEvent(prev, raw, 'pool_reserves');
    return prev;
  }

  const updatedOutcome = applyAmmActivityToOutcome(currentOutcome, raw, _args, outcomeMint);
  const poolChanged =
    currentOutcome.currentPoolState.quoteVaultReserveD6 !== quoteReserveD6 ||
    currentOutcome.currentPoolState.outcomeVaultReserveD6 !== outcomeReserveD6;
  const nextOutcome = poolChanged
    ? {
        ...updatedOutcome,
        currentPoolState: {
          ...currentOutcome.currentPoolState,
          quoteVaultReserveD6: quoteReserveD6,
          outcomeVaultReserveD6: outcomeReserveD6,
        },
      }
    : updatedOutcome;
  const outcomeChanged = nextOutcome !== currentOutcome;
  const liquidityChanged = prev.liquidityD6 !== quoteReserveD6;

  if (!outcomeChanged && !liquidityChanged) return prev;

  return {
    ...prev,
    ...(liquidityChanged ? { liquidityD6: quoteReserveD6 } : {}),
    ...(outcomeChanged
      ? {
          outcomes: prev.outcomes.map((outcome, index) =>
            index === outcomeIndex ? nextOutcome : outcome,
          ),
        }
      : {}),
  };
}

function isAmmPoolState(state: object | undefined): state is AmmPoolState {
  return (
    state !== undefined &&
    'quoteVaultReserveD6' in state &&
    typeof state.quoteVaultReserveD6 === 'string' &&
    'outcomeVaultReserveD6' in state &&
    typeof state.outcomeVaultReserveD6 === 'string'
  );
}

function positiveIntegerField(raw: Record<string, unknown>, key: string): string | null {
  const value = readIntegerString(raw, key);
  return value !== null && BigInt(value) > 0n ? value : null;
}

function rejectMalformedPoolEvent(
  prev: { readonly pubkey?: string },
  raw: Record<string, unknown>,
  reason: string,
): void {
  sdkEvents.emit('applyEvent:error', {
    specName: 'useGetMarket',
    subject: typeof raw.subject === 'string' ? raw.subject : undefined,
    queryKey: ['markets', prev.pubkey],
    error: 'market.amm_pool_event_rejected',
    reason,
  });
}
